import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import InputField from "@client/components/ui/InputField";
import Modal from "@client/components/ui/Modal";
import licensorImage from "@client/components/ui/sharedImages/licensor.svg";
import licensorStore from "@client/stores/LicensorStore";
import notificationStore from "@client/stores/NotificationStore";
import Style from "@client/utils/Style";
import { InputField as InputFieldType } from "@typings/InputField";

import inputFieldsSchema, { ChangePasswordInput } from "./InputFields";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0;
`;
const LicensorImage = styled.div`
  ${Style.exactSize("96px")}
  background: url('${licensorImage}') center no-repeat;
`;
const Fields = styled.form`
  display: flex;
  flex-wrap: wrap;
  margin: 0 32px;
  justify-content: space-between;
  margin-top: -16px;
  > div {
    margin-top: 16px;
  }
`;
const Info = styled.div`
  margin: 0 32px;
  margin-top: 48px;
  font-weight: ${Style.fontWeights.light};
`;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 32px;
`;
type State = {
  inputFields: InputFieldType[];
  highlightedFields: string[];
  goodPassword: boolean;
};
type Props = {
  onRequestClose: () => void;
};

@observer
class ChangePasswordModal extends React.Component<Props, State> {
  state: State = {
    inputFields: [],
    highlightedFields: [],
    goodPassword: false,
  };

  get saving(): boolean {
    return licensorStore.updating;
  }

  UNSAFE_componentWillMount() {
    this.setState({ inputFields: [...inputFieldsSchema.FIELDS] });
  }

  handleFieldChange(field: InputFieldType, value: any) {
    const inputFields = this.state.inputFields.map(f =>
      f.name === field.name ? { ...f, value } : { ...f },
    );
    this.setState({ inputFields });
  }

  async handleSaveClick() {
    const messages: string[] = [];
    const highlightedFields = this.state.inputFields
      .filter(f => {
        const validateFunc = f.validate;
        if (validateFunc) {
          const newField =
            f.name === "password"
              ? {
                  ...f,
                  value: this.state.goodPassword ? f.value : "",
                }
              : f;
          const result = validateFunc(newField, this.state.inputFields);
          if (typeof result === "string") {
            messages.push(result);
            return true;
          }
          return !result;
        }

        if (!f.required || f.value) {
          return false;
        }
        return true;
      })
      .map(f => f.name);
    this.setState({ highlightedFields });
    if (highlightedFields.length) {
      if (messages.length) {
        notificationStore.alert(messages[0], "error");
      }
      return;
    }
    const changePasswordInput: ChangePasswordInput =
      inputFieldsSchema.buildData(this.state.inputFields);

    if (!(await licensorStore.updatePassword(changePasswordInput.password))) {
      return;
    }
    await licensorStore.loadTokenInfo();
    this.props.onRequestClose();
  }

  handlePasswordStrengthChangeScore(score: number) {
    const { goodPassword } = this.state;
    if (score >= 2 && !goodPassword) {
      this.setState({ goodPassword: true });
    } else if (score < 2 && goodPassword) {
      this.setState({ goodPassword: false });
    }
  }

  renderButtons() {
    return (
      <Buttons>
        <Button
          secondary
          large
          onClick={this.props.onRequestClose}
          disabled={this.saving}
        >
          Cancel
        </Button>
        {this.saving ? (
          <ButtonLoading large>Updating...</ButtonLoading>
        ) : (
          <Button
            large
            onClick={() => {
              this.handleSaveClick();
            }}
          >
            Update
          </Button>
        )}
      </Buttons>
    );
  }

  renderFields() {
    return (
      <Fields>
        {this.state.inputFields.map(field => {
          return (
            <InputField
              key={field.name}
              field={field}
              disabled={this.saving}
              highlight={
                !!this.state.highlightedFields.find(name => field.name === name)
              }
              highlightLabel=""
              onChange={value => {
                this.handleFieldChange(field, value);
              }}
              showPasswordStrength={field.name === "password"}
              onPasswordStrengthChangeScore={score => {
                this.handlePasswordStrengthChangeScore(score);
              }}
            />
          );
        })}
      </Fields>
    );
  }

  renderInfo() {
    return (
      <Info>
        After changing your password, you will be logged out and will need to
        login again.
      </Info>
    );
  }

  render() {
    return (
      <Modal
        isOpen
        title="Change Password"
        onRequestClose={this.props.onRequestClose}
      >
        <Wrapper>
          <Header>
            <LicensorImage />
          </Header>
          {this.renderFields()}
          {this.renderInfo()}
          {this.renderButtons()}
        </Wrapper>
      </Modal>
    );
  }
}

export default ChangePasswordModal;
